/**
 * Number/date formatting for Norwegian display.
 *
 * Money is stored as strings throughout the system (no float drift) but
 * formatted as locale-aware numbers for display. Always pair the symbol
 * with `tabular-nums` CSS class for clean column alignment.
 *
 * Locale-specific *strings* (today/yesterday/…) come from `../i18n`; this
 * file owns *formatting* (locale code, separators, date order) only.
 */

import { t } from "../i18n";
import { getCurrentCurrency } from "./settings";

const NOK_FORMAT = new Intl.NumberFormat("nb-NO", {
  style: "currency",
  currency: "NOK",
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const NUMBER_FORMAT = new Intl.NumberFormat("nb-NO", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const DATE_FORMAT = new Intl.DateTimeFormat("nb-NO", {
  day: "numeric",
  month: "short",
  year: "numeric",
});

const SHORT_DATE_FORMAT = new Intl.DateTimeFormat("nb-NO", {
  day: "numeric",
  month: "short",
});

const NUMERIC_DATE_FORMAT = new Intl.DateTimeFormat("nb-NO", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
});

const MONTH_YEAR_FORMAT = new Intl.DateTimeFormat("nb-NO", {
  month: "long",
  year: "numeric",
});

export function formatCurrency(amount: string | number, currency?: string): string {
  const value = typeof amount === "string" ? Number.parseFloat(amount) : amount;
  const code = currency ?? getCurrentCurrency();
  if (code === "NOK") return NOK_FORMAT.format(value);
  return new Intl.NumberFormat("nb-NO", {
    style: "currency",
    currency: code,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value);
}

export function formatNumber(amount: string | number): string {
  const value = typeof amount === "string" ? Number.parseFloat(amount) : amount;
  return NUMBER_FORMAT.format(value);
}

export function formatDate(iso: string): string {
  return DATE_FORMAT.format(new Date(iso));
}

export function formatShortDate(iso: string): string {
  return SHORT_DATE_FORMAT.format(new Date(iso));
}

export function formatNumericDate(iso: string): string {
  return NUMERIC_DATE_FORMAT.format(new Date(iso));
}

export function formatMonthYear(iso: string): string {
  // Capitalize first letter (nb-NO returns lowercase month names)
  const formatted = MONTH_YEAR_FORMAT.format(new Date(iso));
  return formatted.charAt(0).toUpperCase() + formatted.slice(1);
}

export function formatPercent(fraction: number): string {
  if (fraction <= 0) return "0 %";
  const pct = fraction * 100;
  // Non-zero values that round to 0 % should still hint at presence
  // instead of looking like nothing. Same on the high end (99.6 %
  // shouldn't show 100 % when the slice clearly isn't the whole pie).
  if (pct > 0 && pct < 1) return "< 1 %";
  if (pct > 99 && pct < 100) return "> 99 %";
  return `${Math.round(pct)} %`;
}

/**
 * Relative time: "i dag", "i går", "for 3 dager siden", "12. mai 2026".
 */
export function formatRelative(iso: string): string {
  const date = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / 86_400_000);

  if (diffDays === 0) return t.time.today;
  if (diffDays === 1) return t.time.yesterday;
  if (diffDays < 7) return t.time.daysAgo(diffDays);
  if (diffDays < 30) return t.time.weeksAgo(Math.floor(diffDays / 7));
  return formatDate(iso);
}

/**
 * Relative future: "i dag", "i morgen", "om 3 dager", "om 4 uker", "12. aug 2026".
 * Mirror of `formatRelative` for forward-looking dates (e.g. consent expiry).
 */
export function formatFromNow(iso: string): string {
  const date = new Date(iso);
  const now = new Date();
  const diffDays = Math.floor((date.getTime() - now.getTime()) / 86_400_000);

  if (diffDays <= 0) return t.time.today;
  if (diffDays === 1) return t.time.tomorrow;
  if (diffDays < 7) return t.time.daysFromNow(diffDays);
  if (diffDays < 60) return t.time.weeksFromNow(Math.floor(diffDays / 7));
  return formatDate(iso);
}

export function formatPeriod(start: string, end: string): string {
  const startDate = new Date(start);
  const endDate = new Date(end);
  // Same month? "1.–31. mai 2026". Different months? "1. apr.–15. mai 2026".
  if (
    startDate.getMonth() === endDate.getMonth() &&
    startDate.getFullYear() === endDate.getFullYear()
  ) {
    return `${startDate.getDate()}.–${endDate.getDate()}. ${MONTH_YEAR_FORMAT.format(endDate)}`;
  }
  return `${formatShortDate(start)}–${formatDate(end)}`;
}
